/**
 * product-description-accordion.js
 *
 * Reshapes a .product-description into:
 *   1. First paragraph — left visible, ungrouped.
 *   2. A native <details> accordion below it. Sections split on
 *      "spaced-caps" headers (e.g. "D E T A I L S", "F I T"). The
 *      content between the first paragraph and the first header
 *      becomes a "PRODUCT INFO" section.
 *
 * Spaced-caps detection: a <p> whose tokens (split on whitespace) are
 * all single uppercase A–Z letters (with `&`/`/`/`-`/`+` allowed as
 * connectors), with at least 3 letters.
 *
 * Runs after sizing-chart.js (which extracts tables/imgs/paragraph
 * tables) so charts are already lifted out of the description by the
 * time we build the accordion.
 */
(function () {
  'use strict';

  function isSpacedCapsHeader(text) {
    text = (text || '').trim().replace(/\s+/g, ' ');
    if (!text) return false;
    var tokens = text.split(' ');
    if (tokens.length < 3) return false;
    var letterCount = 0;
    for (var i = 0; i < tokens.length; i++) {
      var t = tokens[i];
      if (t === '&' || t === '/' || t === '-' || t === '+') continue;
      if (!/^[A-Z]$/.test(t)) return false;
      letterCount++;
    }
    return letterCount >= 3;
  }

  // Header candidate: any direct-child block element whose visible text
  // matches the spaced-caps pattern. Container/structural elements are
  // ineligible because their text content is just incidental.
  function isLikelyHeaderElement(el) {
    if (!el || el.nodeType !== 1) return false;
    if (/^(UL|OL|TABLE|IMG|PICTURE|VIDEO|IFRAME|HR|BR)$/.test(el.tagName)) return false;
    if (el.querySelector && el.querySelector('ul, ol, table, img, picture, video, iframe')) return false;
    return isSpacedCapsHeader(el.textContent || '');
  }

  function buildAccordion(desc) {
    if (!desc || desc.dataset.rpAccordionBuilt) return;
    var children = Array.prototype.slice.call(desc.children);
    if (children.length < 2) return;

    // First non-empty child stays visible. Accept <p> OR any element that
    // carries real text (some descriptions open with <div> wrappers).
    var firstP = -1;
    for (var i = 0; i < children.length; i++) {
      if ((children[i].textContent || '').trim()) {
        firstP = i;
        break;
      }
    }
    if (firstP === -1) return;

    // Split everything after firstP into sections at each spaced-caps
    // header — works whether the header lives in a <p>, <div>, or <h*>.
    // PRODUCT INFO is always the first section so the structure stays
    // predictable; later header sections only stay if they collected
    // content beneath them.
    var sections = [];
    var current = { title: 'PRODUCT INFO', nodes: [] };
    sections.push(current);
    for (var j = firstP + 1; j < children.length; j++) {
      var n = children[j];
      var t = (n.textContent || '').trim();
      if (isLikelyHeaderElement(n)) {
        current = { title: t.replace(/\s+/g, ' '), nodes: [] };
        sections.push(current);
      } else {
        current.nodes.push(n);
      }
    }
    // Drop any non-PRODUCT-INFO sections that ended up empty.
    sections = sections.filter(function (s, i) {
      return i === 0 || s.nodes.length > 0;
    });
    // If PRODUCT INFO is empty AND there are no other sections, nothing
    // to accordion — bail.
    if (sections.length === 1 && sections[0].nodes.length === 0) return;

    // Build accordion DOM, moving each section's nodes into the body.
    var accordion = document.createElement('div');
    accordion.className = 'rp-pd-accordion';
    sections.forEach(function (sec) {
      var det = document.createElement('details');
      det.className = 'rp-pd-accordion__item';
      // Native exclusive-accordion behavior in browsers that support it
      // (Chrome 120+/Safari 17.5+/Firefox 123+) — opens one, closes others
      // even without JS.
      det.setAttribute('name', 'rp-pd-accordion');

      var sum = document.createElement('summary');
      sum.className = 'rp-pd-accordion__head';
      var titleSpan = document.createElement('span');
      titleSpan.className = 'rp-pd-accordion__title';
      titleSpan.textContent = sec.title;
      var caret = document.createElement('span');
      caret.className = 'rp-pd-accordion__caret';
      caret.setAttribute('aria-hidden', 'true');
      caret.textContent = '+';
      sum.appendChild(titleSpan);
      sum.appendChild(caret);

      var body = document.createElement('div');
      body.className = 'rp-pd-accordion__body';
      sec.nodes.forEach(function (n) { body.appendChild(n); });

      det.appendChild(sum);
      det.appendChild(body);
      accordion.appendChild(det);
    });

    // Append the accordion (moved nodes now live inside it).
    desc.appendChild(accordion);

    // Spaced-caps header <p>s weren't pushed into any section, so they're
    // still direct children of desc — remove them now.
    for (var k = firstP + 1; k < children.length; k++) {
      var nn = children[k];
      if (nn.parentNode === desc) desc.removeChild(nn);
    }

    // Hook up the click handler — animated open/close, one section at a time.
    wireAccordion(accordion);

    // The original "Read more" truncation no longer makes sense once the
    // long content is gated behind disclosure.
    desc.classList.remove('readmore', 'closed');
    desc.dataset.rpAccordionBuilt = '1';
  }

  function openSection(det) {
    var body = det.querySelector('.rp-pd-accordion__body');
    if (!body) return;
    det.setAttribute('open', '');
    body.style.height = '0px';
    /* eslint-disable-next-line no-unused-expressions */
    body.offsetHeight; // force reflow so the next height: target actually animates
    body.style.height = body.scrollHeight + 'px';
    body.addEventListener('transitionend', function onEnd(e) {
      if (e.propertyName !== 'height') return;
      body.style.height = '';
      body.removeEventListener('transitionend', onEnd);
    });
  }

  function closeSection(det) {
    var body = det.querySelector('.rp-pd-accordion__body');
    if (!body) return;
    body.style.height = body.scrollHeight + 'px';
    /* eslint-disable-next-line no-unused-expressions */
    body.offsetHeight;
    body.style.height = '0px';
    body.addEventListener('transitionend', function onEnd(e) {
      if (e.propertyName !== 'height') return;
      det.removeAttribute('open');
      body.style.height = '';
      body.removeEventListener('transitionend', onEnd);
    });
  }

  function wireAccordion(accordion) {
    var items = accordion.querySelectorAll('.rp-pd-accordion__item');
    items.forEach(function (det) {
      var sum = det.querySelector('summary');
      if (!sum) return;
      sum.addEventListener('click', function (e) {
        // Take full control — prevent the native instant toggle so the
        // height animation drives the open/close instead.
        e.preventDefault();
        var isOpen = det.hasAttribute('open');
        if (isOpen) {
          closeSection(det);
          return;
        }
        // Close any currently-open siblings first so only one is visible.
        items.forEach(function (other) {
          if (other !== det && other.hasAttribute('open')) closeSection(other);
        });
        openSection(det);
      });
    });
  }

  function init() {
    if (!document.body.classList.contains('template-product')
        && !document.querySelector('[itemtype*="schema.org/Product"]')) {
      return;
    }

    var wrapper = document.querySelector('.product-details-wrapper');

    // Schedule the reveal BEFORE building the accordion, so a thrown
    // exception inside buildAccordion can't leave the page stuck at
    // opacity: 0 forever. Double rAF gives the browser a chance to
    // paint opacity: 0 once so the transition has a "from" frame;
    // setTimeout fires it as a backstop even if rAF never runs (e.g.
    // a background tab whose rAF callbacks are throttled to never).
    //
    // CRITICAL: flag the reveal on <body>, NOT on .product-details-wrapper.
    // The wrapper lives inside <wetheme-product-form inline-template>, a
    // Vue mount. With inline-template, Vue uses the server HTML as its
    // template and re-renders on mount — a class we add to the wrapper
    // between HTML parse and Vue mount can get clobbered by that re-render.
    // <body> is outside the Vue scope, so the flag is stable. The matching
    // CSS in custom.css reads `body.rp-pd-ready .product-details-wrapper ...`.
    function reveal() {
      if (!document.body.classList.contains('rp-pd-ready')) {
        document.body.classList.add('rp-pd-ready');
      }
    }
    requestAnimationFrame(function () {
      requestAnimationFrame(reveal);
    });
    setTimeout(reveal, 400);

    try {
      var desc = document.querySelector('.product-description');
      if (desc) buildAccordion(desc);
    } catch (e) {
      // Swallow — accordion failing must not block the reveal.
      if (window.console && console.warn) console.warn('[accordion]', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
